// QR Kiosk v2 — KV2-3 grouped part picker (bug #8). Reads s.public.machineParts
// (the page dispatches getMachineParts). REAL loading state until /v1/machine-parts
// resolves — the v1 hardcoded MACHINE_PART_GROUPS fallback (whose options had no
// categoryId and were non-selectable) is NOT carried; that dead fallback IS bug #8.
// Full-screen takeover; Round-6: PORTALED into the kiosk overlay root (escapes the
// CreateReportV2 kv2-enter form subtree). Retry affordance appears only after 5s of
// empty parts (KV2-3-b).
import React, { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import { useBreakpoint } from "../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../shared/hooks/useTranslation";
import { Glyph, GD } from "../shared/glyphs";
import { SkeletonBlock } from "../shared/SkeletonBlocks";
import { BackPill } from "../shared/BackPill";
import { useKioskOverlayRoot } from "../shared/kioskOverlayRoot";
import { KCHROME_H, TAKEOVER_LIST_PAD } from "../shared/kioskNav";
import { inputStyle } from "./formAtoms";
import { findPartTranslationKey } from "./reportUtils";

export interface PartPickerProps {
  value?: string;
  onChange: (categoryId: string) => void;
  error?: boolean;
  /** Page-owned retry (re-dispatch getMachineParts). */
  onRetry?: () => void;
}

// v1 camelCase transform (same as findPartTranslationKey).
const camelKey = (code: string) => code.replace(/_([a-z])/g, (_m, c: string) => c.toUpperCase());

export function PartPicker({ value, onChange, error, onRetry }: PartPickerProps) {
  const machineParts = useAppSelector((s) => s.public.machineParts);
  const { translate } = useTranslation(["public", "servicing"]);
  const { isPhone, isTablet } = useBreakpoint();
  const overlayRoot = useKioskOverlayRoot();
  const [open, setOpen] = useState(false);
  const [showRetry, setShowRetry] = useState(false);

  const loading = machineParts.length === 0;

  // Retry affordance only after 5s of empty parts — the fast path never flashes it.
  useEffect(() => {
    if (!loading) {
      setShowRetry(false);
      return;
    }
    const t = setTimeout(() => setShowRetry(true), 5000);
    return () => clearTimeout(t);
  }, [loading]);

  const selectedLabel = useMemo(() => {
    const key = findPartTranslationKey(machineParts, value);
    return key ? translate(`servicing:machineParts.parts.${key}`) : undefined;
  }, [machineParts, value, translate]);

  const columnMaxWidth: number | string = isPhone ? "100%" : isTablet ? 560 : 640;

  return (
    <>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          disabled={loading}
          aria-busy={loading || undefined}
          onClick={() => !loading && setOpen(true)}
          style={{
            ...inputStyle(error),
            display: "flex",
            alignItems: "center",
            gap: 8,
            textAlign: "left",
            cursor: loading ? "default" : "pointer",
          }}
        >
          {loading ? (
            <>
              <SkeletonBlock w={110} h={12} />
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--faint)" }}>
                {translate("kioskV2PartsLoading")}
              </span>
            </>
          ) : (
            <>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 15,
                  fontWeight: 500,
                  color: selectedLabel ? "var(--ink)" : "var(--faint)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedLabel || translate("affectedMachinePart")}
              </span>
              <span style={{ color: "var(--muted)", display: "inline-flex", transform: "rotate(90deg)", flexShrink: 0 }}>
                <Glyph d={GD.chev} size={14} />
              </span>
            </>
          )}
        </button>
        {loading && showRetry && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            style={{
              height: 52,
              padding: "0 14px",
              borderRadius: 11,
              border: "1px solid var(--border)",
              background: "var(--card)",
              color: "var(--ink-2)",
              fontFamily: "inherit",
              fontSize: 12.5,
              fontWeight: 600,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {translate("kioskV2Retry")}
          </button>
        )}
      </div>

      {/* Part picker — full-screen takeover BELOW the 50px chrome (§7.1-B). The chrome
          header stays fully visible/undimmed; the last part row scrolls clear of the pill. */}
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal container={overlayRoot}>
        <Dialog.Overlay style={{ position: "fixed", top: KCHROME_H, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", zIndex: 40 }} />
        <Dialog.Content
          aria-describedby={undefined}
          className="kv2-sheet-up"
          style={{
            position: "fixed",
            top: KCHROME_H,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 41,
            maxWidth: columnMaxWidth,
            margin: "0 auto",
            background: "var(--card)",
            borderTop: "1px solid var(--border)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Dialog.Title asChild>
            <div style={{ flexShrink: 0, padding: "14px 16px 8px", fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>
              {translate("affectedMachinePart")}
            </div>
          </Dialog.Title>
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: `6px 0 calc(${TAKEOVER_LIST_PAD}px + env(safe-area-inset-bottom, 0px))`,
            }}
          >
            {machineParts.map((group) => (
              <div key={group.groupKey}>
                <div
                  className="eyebrow"
                  style={{ position: "sticky", top: 0, background: "var(--card)", padding: "10px 16px 4px", color: "var(--muted)", zIndex: 1 }}
                >
                  {translate(`servicing:machineParts.groups.${group.groupKey}`)}
                </div>
                {group.parts.map((part) => {
                  const selected = part.id === value;
                  return (
                    <button
                      key={part.id}
                      type="button"
                      onClick={() => {
                        onChange(part.id);
                        setOpen(false);
                      }}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        minHeight: 54,
                        padding: "0 16px",
                        border: "none",
                        background: selected ? "var(--tier-accent-soft)" : "transparent",
                        fontSize: 16,
                        fontWeight: selected ? 700 : 500,
                        color: "var(--ink)",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        textAlign: "left",
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {translate(`servicing:machineParts.parts.${camelKey(part.code)}`)}
                      </span>
                      {selected && (
                        <span style={{ color: "var(--tier-accent)", display: "inline-flex", flexShrink: 0 }}>
                          <Glyph d={GD.check} size={16} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          {/* Escape-without-selecting exit (selecting a part still commits + closes instantly). */}
          <BackPill label={translate("kioskBack")} onBack={() => setOpen(false)} zIndex={42} />
        </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

export default PartPicker;
